import React from 'react'

import {
  Box,
  Button,
  CssBaseline,
  Paper,
  Typography
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  contentColumnMaxWidthSx,
  contentColumnShellSx
} from '../UI/contentColumn'

const DEFAULT_REASON = 'Event paused for inactivity.'
const DEFAULT_RECONNECT_LABEL = 'Rejoin'

/**
 * Full-page overlay rendered when an EVENT_PAUSED message arrives on the event channel.
 *
 * The AEM has been shut down to conserve realtime resources, but its persisted snapshot
 * is intact. Reloading the page re-enters presence on :actions and triggers ANAPI
 * LOAD_EVENT, which rehydrates the AEM from the snapshot.
 *
 * The Rejoin button is only rendered when `showRejoin` is true (typically gated on the
 * caller having an active participant). Spectators see the paused notice without the
 * button — they can refresh manually if they want to observe the resumed event.
 */
const EventPausedPage = ({ anearEvent, reason, reconnectLabel, showRejoin = true, onRejoin }) => {
  const iconUrl = anearEvent && anearEvent.zone?.app?.iconUrl
  const shortName = anearEvent && anearEvent.zone?.app?.shortName
  const message = reason || DEFAULT_REASON
  const buttonLabel = reconnectLabel || DEFAULT_RECONNECT_LABEL
  const handleRejoin = onRejoin || (() => window.location.reload())

  return (
    <Box
      // px keeps the gutters phones have always had; from sm up the column cap sets the width.
      sx={{ ...contentColumnShellSx, px: 2 }}
      data-anear-content-column=""
    >
      <Box sx={contentColumnMaxWidthSx}>
        <CssBaseline />
        <Paper
          sx={{
            mt: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: 2,
            px: 3,
            pb: 3
          }}
        >
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
            {iconUrl && (
              <img
                style={{
                  height: '90px',
                  float: 'left',
                  marginRight: '16px'
                }}
                src={iconUrl}
                alt={shortName || 'Event'}
              />
            )}
            <Typography variant="h6">{message}</Typography>
          </Box>
          {showRejoin && (
            <Box sx={{ width: '100%' }}>
              <Button
                sx={{ mt: 2 }}
                size="medium"
                color="primary"
                variant="contained"
                onClick={handleRejoin}
              >
                <RefreshIcon sx={{ mr: 1, height: 20, width: 20 }} />
                {buttonLabel}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  )
}

export default EventPausedPage
